export interface SpriteCue {
    startTime: number;
    endTime: number;
    spriteFile: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface SpriteInfo {
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

function parseVttTimestamp(timestamp: string): number {
    const parts = timestamp.split(':');
    if (parts.length !== 3) return 0;

    const [hours, minutes, secondsPart] = parts;
    const [seconds, milliseconds = '0'] = secondsPart.split('.');

    return (
        Number(hours) * 3600 +
        Number(minutes) * 60 +
        Number(seconds) +
        Number(milliseconds) / 1000
    );
}

function parseSpriteLine(line: string): {
    spriteFile: string;
    x: number;
    y: number;
    width: number;
    height: number;
} | null {
    const hashIndex = line.indexOf('#xywh=');
    if (hashIndex === -1) return null;

    const spriteFile = line.substring(0, hashIndex);
    const coords = line
        .substring(hashIndex + 6)
        .split(',')
        .map(Number);

    if (coords.length !== 4 || coords.some(isNaN)) return null;

    return {
        spriteFile,
        x: coords[0],
        y: coords[1],
        width: coords[2],
        height: coords[3],
    };
}

function parseCueBlock(timeLine: string, spriteLine: string): SpriteCue | null {
    const timeParts = timeLine.split('-->').map((s) => s.trim());
    if (timeParts.length !== 2) return null;

    const spriteData = parseSpriteLine(spriteLine);
    if (!spriteData) return null;

    return {
        startTime: parseVttTimestamp(timeParts[0]),
        endTime: parseVttTimestamp(timeParts[1]),
        ...spriteData,
    };
}

export function parseVtt(vttContent: string): SpriteCue[] {
    const lines = vttContent.split('\n').map((line) => line.trim());
    const cues: SpriteCue[] = [];

    for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes('-->') && lines[i + 1]) {
            const cue = parseCueBlock(lines[i], lines[i + 1]);
            if (cue) cues.push(cue);
        }
    }

    return cues;
}

export function findCueForTime(
    cues: SpriteCue[],
    time: number,
): SpriteCue | null {
    if (cues.length === 0) return null;

    let left = 0;
    let right = cues.length - 1;

    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const cue = cues[mid];

        if (time >= cue.startTime && time < cue.endTime) {
            return cue;
        }

        if (time < cue.startTime) {
            right = mid - 1;
        } else {
            left = mid + 1;
        }
    }

    return time < cues[0].startTime ? cues[0] : cues[cues.length - 1];
}

export function extractSpriteIndex(spriteFile: string): number {
    return parseInt(spriteFile.replace('sprite_', '').replace('.jpg', ''), 10);
}

export function getUniqueSpriteFiles(cues: SpriteCue[]): string[] {
    return Array.from(new Set(cues.map((cue) => cue.spriteFile)));
}
